// src/pages/PipelineCotizacion.jsx
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import Cargando from "../components/Cargando";
import * as XLSX from "xlsx";
import { auth } from "../firebase";

const NAV    = "#3A3C7A";
const GREEN  = "#1D9E75";
const AMBER  = "#F59E0B";
const RED    = "#DC2626";
const GRAY   = "#6B7280";
const LIGHTBG = "#F8F9FF";

const DRAFT_KEY = "pipeline_cotizacion_borrador";
function leerBorrador() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null") || {}; }
  catch { return {}; }
}

const PASOS = [
  { n: 1, label: "Requerimiento" },
  { n: 2, label: "Proveedor"     },
  { n: 3, label: "Márgenes"      },
  { n: 4, label: "Crear"         },
];

function SectionTitle({ color = NAV, label, count }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 0 3px ${color}25`, flexShrink: 0 }} />
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#374151" }}>{label}</span>
      {count != null && (
        <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: color + "18", color }}>{count}</span>
      )}
      <div style={{ flex: 1, height: "0.5px", background: "#e2e8f0" }} />
    </div>
  );
}

function ChipMargen({ pct }) {
  const color = pct >= 10 ? GREEN : pct >= 6 ? AMBER : RED;
  return (
    <span style={{ background: color + "20", color, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700, display: "inline-block" }}>
      {pct}%
    </span>
  );
}

function BtnPrimary({ onClick, disabled, children, color = NAV, glow }) {
  const bg   = color === GREEN
    ? "linear-gradient(135deg, #22c55e, #16a34a)"
    : "linear-gradient(135deg, #1E3A5F, #3A3C7A)";
  const shadow = color === GREEN
    ? "0 4px 14px rgba(34,197,94,0.35)"
    : "0 4px 14px rgba(58,60,122,0.35)";
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        background: disabled ? "#9ca3af" : bg,
        boxShadow: disabled ? "none" : shadow,
        color: "#fff", border: "none", borderRadius: 9, padding: "10px 24px",
        fontWeight: 700, fontSize: 14, cursor: disabled ? "not-allowed" : "pointer",
        transition: "box-shadow 200ms, transform 150ms",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.boxShadow = shadow.replace("0.35", "0.55"); }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.boxShadow = shadow; }}
    >
      {children}
    </button>
  );
}

function BtnSecondary({ onClick, children }) {
  return (
    <button onClick={onClick}
      style={{ background: "#f3f4f6", color: GRAY, border: "1px solid #e5e7eb", borderRadius: 9, padding: "10px 20px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
      {children}
    </button>
  );
}

// ── Helpers puros (sin estado) — usados tanto por la creación real como por el preview ──
const limpiarDescripcion = (s) => (s || "").replace(/\s*[—-]+\s*[\d,.]+\s*\w*\s*$/, "").trim();

// Conversión precio↔margen — antes repetida letra por letra en 2 puntos de recalcularFila().
// Misma fórmula, mismo redondeo: extraerla no cambia ningún valor calculado.
function margenDesdeVenta(precioBase, precioVenta) {
  const pb = parseFloat(precioBase) || 0;
  const pv = parseFloat(precioVenta) || 0;
  if (pb <= 0 || pv <= 0) return null;
  return parseFloat(((pv - pb) / pv * 100).toFixed(1));
}

function ventaDesdeMargen(precioBase, margen) {
  const pb = parseFloat(precioBase) || 0;
  const m  = parseFloat(margen) || 0;
  if (pb <= 0 || m >= 100) return null;
  return parseFloat((pb / (1 - m / 100)).toFixed(2));
}

function calcularPrecioFinal(item, fuente) {
  const usarPipelinePrecios = fuente === "pipeline" && (item.estado === "encontrado" || item.estado === "revisar");
  const pbPipeline = parseFloat(item.precio_base) || parseFloat(item.pb_edit) || 0;
  const pvPipeline = parseFloat(item.precio_venta_edit) > 0
    ? parseFloat(item.precio_venta_edit)
    : (parseFloat(item.pv_edit) || 0);
  const pb = usarPipelinePrecios ? pbPipeline : (parseFloat(item.pb_edit) || 0);
  const pv = usarPipelinePrecios ? pvPipeline : (parseFloat(item.pv_edit) || parseFloat(item.precio_venta_edit) || 0);
  return { pb, pv };
}

// Construye exactamente lo que crearCotizacion() enviaría a la API, sin llamar a ningún endpoint.
function construirLineasPreview(itemsVerif, fuente) {
  const incluidas = [];
  const omitidas  = [];
  for (const item of itemsVerif) {
    if (item.estado === "pendiente_manual") { omitidas.push(item); continue; }
    const { pv } = calcularPrecioFinal(item, fuente);
    const cantidad  = item.cantidad || 1;
    const impuesto  = parseFloat(item.impuesto_edit ?? 18);
    const descBase  = item.estado === "nuevo"
      ? (item.descripcion_proveedor || item.descripcion)
      : (item.buyer_desc || item.descripcion_proveedor || item.descripcion);
    const subtotal  = pv * cantidad;
    const igv       = subtotal * (impuesto / 100);
    incluidas.push({
      descripcion: limpiarDescripcion(descBase),
      cantidad, unidad: item.unidad, pv, subtotal, igv,
      total: subtotal + igv, esNuevo: item.estado === "nuevo",
    });
  }
  return { incluidas, omitidas };
}

export default function PipelineCotizacion() {
  // Leer borrador guardado (solo en el primer render)
  const borradorRef = useRef(null);
  if (borradorRef.current === null) borradorRef.current = leerBorrador();
  const d = borradorRef.current;
  // Solo hay borrador real si hay contenido significativo guardado
  const hayBorrador = (d.paso > 1) || (d.productos?.length > 0) || (d.comparacion?.length > 0) || (d.itemsVerif?.length > 0);

  const [paso,       setPaso]       = useState(() => d.paso       || 1);
  const [cargando,   setCargando]   = useState(false);

  // Header — cliente
  const [clienteQ,     setClienteQ]     = useState(() => d.clienteQ    || "");
  const [clientesRes,  setClientesRes]  = useState([]);
  const [clienteSel,   setClienteSel]   = useState(() => d.clienteSel  || null);
  const [buscandoCli,  setBuscandoCli]  = useState(false);

  // Paso 1
  const [inputTipo,    setInputTipo]    = useState(() => d.inputTipo   || "texto");
  const [textoReq,     setTextoReq]     = useState(() => d.textoReq    || "");
  const [imagenB64,    setImagenB64]    = useState(null);   // no se persiste (grande)
  const [imagenMime,   setImagenMime]   = useState("image/jpeg");
  const [archivoReq,   setArchivoReq]   = useState(null);   // no se persiste (grande)
  const [productos,    setProductos]    = useState(() => d.productos   || []);

  // Paso 2
  const [inputTipo2,    setInputTipo2]    = useState(() => d.inputTipo2    || "texto");
  const [textoProvee,   setTextoProvee]   = useState(() => d.textoProvee   || "");
  const [imagenProveeB64,  setImagenProveeB64]  = useState(null);   // no se persiste
  const [imagenProveeMime, setImagenProveeMime] = useState("image/jpeg");
  const [archivoProvee, setArchivoProvee] = useState(null);   // no se persiste
  const [comparacion,     setComparacion]     = useState(() => d.comparacion     || []);
  const [proveedorConIGV, setProveedorConIGV] = useState(() => d.proveedorConIGV ?? false);

  // Identidad del proveedor (paso 2) — distinto de proveedorConIGV, que solo dice si SUS
  // precios incluyen IGV. Esto es quién es, para poder asignarlo a los productos nuevos
  // que se creen en buyer al armar la cotización.
  const [proveedorQ,       setProveedorQ]       = useState(() => d.proveedorQ    || "");
  const [proveedoresRes,   setProveedoresRes]   = useState([]);
  const [proveedorSel,     setProveedorSel]     = useState(() => d.proveedorSel  || null);
  const [buscandoProv,     setBuscandoProv]     = useState(false);
  const [creandoProveedor, setCreandoProveedor] = useState(false);
  const [nuevoProvNombre,  setNuevoProvNombre]  = useState("");
  const [nuevoProvEmpresa, setNuevoProvEmpresa] = useState("");
  const [nuevoProvRuc,     setNuevoProvRuc]     = useState("");
  const [guardandoProveedor, setGuardandoProveedor] = useState(false);

  // Paso 3
  const [filas,        setFilas]        = useState(() => d.filas      || []);
  const [fleteTotal,   setFleteTotal]   = useState(() => d.fleteTotal  ?? "");
  const [obsGlobal,    setObsGlobal]    = useState(() => d.obsGlobal   || "");

  // Paso 4
  const [verificando,        setVerificando]        = useState(false);
  const [itemsVerif,         setItemsVerif]         = useState(() => d.itemsVerif         || []);
  const [resultado,          setResultado]          = useState(() => d.resultado          || null);
  const [preciosConIGVBuyer, setPreciosConIGVBuyer] = useState(() => d.preciosConIGVBuyer ?? false);
  const [showPrecioModal,    setShowPrecioModal]    = useState(false);
  const [showPreviewModal,   setShowPreviewModal]   = useState(false);
  const [previewFuente,      setPreviewFuente]      = useState("pipeline");
  const [filtroEstado,       setFiltroEstado]       = useState("todos");
  const [modoGlobalMatch,    setModoGlobalMatch]    = useState(null); // "ia" | "respaldo_palabras" | null

  // Moneda global de la cotización
  const [moneda,     setMoneda]     = useState(() => d.moneda     || "PEN");
  const [tipoCambio, setTipoCambio] = useState(() => d.tipoCambio || "");

  // Auto-guardar borrador en localStorage cuando cambia el estado relevante
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        paso, clienteQ, clienteSel, inputTipo, textoReq, productos,
        inputTipo2, textoProvee, comparacion, proveedorConIGV, proveedorQ, proveedorSel,
        filas, fleteTotal, obsGlobal, itemsVerif, resultado, preciosConIGVBuyer,
        moneda, tipoCambio,
      }));
    } catch (_) {}
  }, [paso, clienteQ, clienteSel, inputTipo, textoReq, productos,
      inputTipo2, textoProvee, comparacion, proveedorConIGV, proveedorQ, proveedorSel,
      filas, fleteTotal, obsGlobal, itemsVerif, resultado, preciosConIGVBuyer,
      moneda, tipoCambio]);

  const fileRef      = useRef();
  const archivoRef   = useRef();
  const archivoRef2  = useRef();
  const imagenRef2   = useRef();

  // Parsear Excel → texto tabular
  const excelATexto = (buffer) => {
    const wb   = XLSX.read(buffer, { type: "array" });
    const hoja = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_csv(hoja);
  };

  // Leer archivo y devolver { nombre, tipo, b64, texto }
  const leerArchivo = (file) => new Promise((resolve, reject) => {
    const ext  = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();
    reader.onerror = reject;

    if (ext === "pdf") {
      reader.onload = (e) => resolve({ nombre: file.name, tipo: "pdf", b64: btoa(String.fromCharCode(...new Uint8Array(e.target.result))), texto: null });
      reader.readAsArrayBuffer(file);
    } else if (["xlsx", "xls"].includes(ext)) {
      reader.onload = (e) => resolve({ nombre: file.name, tipo: "excel", b64: null, texto: excelATexto(e.target.result) });
      reader.readAsArrayBuffer(file);
    } else if (["docx", "doc"].includes(ext)) {
      // Word: leer como ArrayBuffer y extraer texto con mammoth dinámicamente
      reader.onload = async (e) => {
        try {
          const mammoth = await import("mammoth");
          const result  = await mammoth.extractRawText({ arrayBuffer: e.target.result });
          resolve({ nombre: file.name, tipo: "word", b64: null, texto: result.value });
        } catch { reject(new Error("No se pudo leer el archivo Word")); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error("Formato no soportado"));
    }
  });

  const api = useCallback(async (body) => {
    const idToken = await auth.currentUser?.getIdToken();
    const resp = await fetch("/api/pipeline-cotizacion", {
      method: "POST",
      headers: {
        "Content-Type":  "application/json",
        "Authorization": `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });
    return resp.json();
  }, []);

  const buscarClientes = async (q) => {
    setClienteQ(q);
    if (q.length < 2) { setClientesRes([]); return; }
    setBuscandoCli(true);
    const data = await api({ accion: "buscar_clientes", q });
    setBuscandoCli(false);
    setClientesRes(data.data || []);
  };

  const buscarProveedores = async (q) => {
    setProveedorQ(q);
    if (q.length < 2) { setProveedoresRes([]); return; }
    setBuscandoProv(true);
    const data = await api({ accion: "buscar_proveedores", q });
    setBuscandoProv(false);
    setProveedoresRes(data.data || []);
  };

  const crearProveedorNuevo = async () => {
    if (!nuevoProvNombre.trim()) { toast.error("Ingresa el nombre del proveedor"); return; }
    setGuardandoProveedor(true);
    try {
      const data = await api({
        accion: "crear_proveedor",
        nombre: nuevoProvNombre.trim(),
        empresa: nuevoProvEmpresa.trim() || null,
        ruc: nuevoProvRuc.trim() || null,
      });
      if (data.success) {
        setProveedorSel(data.data);
        setCreandoProveedor(false);
        setNuevoProvNombre(""); setNuevoProvEmpresa(""); setNuevoProvRuc("");
        setProveedorQ(""); setProveedoresRes([]);
        toast.success(data.reused ? "Ya existía un proveedor con ese RUC — se seleccionó" : "Proveedor creado");
      } else {
        toast.error("No se pudo crear el proveedor");
      }
    } catch {
      toast.error("No se pudo crear el proveedor");
    } finally {
      setGuardandoProveedor(false);
    }
  };

  const organizar = async () => {
    if (inputTipo === "texto"  && !textoReq.trim()) { toast.error("Pega el requerimiento primero"); return; }
    if (inputTipo === "imagen" && !imagenB64)        { toast.error("Sube una imagen primero"); return; }
    if (inputTipo === "archivo" && !archivoReq)      { toast.error("Sube un archivo primero"); return; }
    setCargando(true);
    try {
      const body = { accion: "organizar" };
      if (inputTipo === "imagen") {
        body.imagen_base64 = imagenB64;
        body.tipo_imagen   = imagenMime;
      } else if (inputTipo === "archivo") {
        if (archivoReq.tipo === "pdf") body.pdf_base64 = archivoReq.b64;
        else body.texto = archivoReq.texto;
      } else {
        body.texto = textoReq;
      }
      const data = await api(body);
      if (!data.success) { toast.error("Error al organizar"); return; }
      setProductos(data.productos);
      toast.success(`${data.productos.length} productos organizados`);
    } catch { toast.error("Error de conexión — intentá de nuevo"); }
    finally  { setCargando(false); }
  };

  const copiarParaProveedor = () => {
    const texto = productos
      .map((p, i) => `${i + 1}. ${p.descripcion} — ${p.cantidad ?? "?"} ${p.unidad ?? ""} ${p.notas ? "(" + p.notas + ")" : ""}`.trim())
      .join("\n");
    navigator.clipboard.writeText(texto);
    toast.success("Copiado para proveedor");
  };

  const comparar = async () => {
    if (inputTipo2 === "texto"   && !textoProvee.trim())  { toast.error("Pega la respuesta del proveedor"); return; }
    if (inputTipo2 === "imagen"  && !imagenProveeB64)     { toast.error("Sube una imagen primero"); return; }
    if (inputTipo2 === "archivo" && !archivoProvee)       { toast.error("Sube un archivo primero"); return; }
    setCargando(true);
    try {
      const body = { accion: "comparar", productos, texto_proveedor: "" };
      if (inputTipo2 === "imagen") {
        body.imagen_proveedor      = imagenProveeB64;
        body.tipo_imagen_proveedor = imagenProveeMime;
      } else if (inputTipo2 === "archivo") {
        if (archivoProvee.tipo === "pdf") body.pdf_proveedor = archivoProvee.b64;
        else body.texto_proveedor = archivoProvee.texto;
      } else {
        body.texto_proveedor = textoProvee;
      }
      const data = await api(body);
      if (!data.success) { toast.error("Error al comparar"); return; }
      setComparacion(data.comparacion);
      setFilas(data.comparacion.map((c) => {
        const pb = c.precio_base != null
          ? (proveedorConIGV ? parseFloat((c.precio_base / 1.18).toFixed(4)) : c.precio_base)
          : null;
        const m = c.margen_sugerido ?? 8;
        const pv = pb != null && pb > 0 && m < 100
          ? parseFloat((pb / (1 - m / 100)).toFixed(2))
          : (c.precio_venta ?? "");
        return { ...c, precio_base: pb ?? c.precio_base ?? "", precio_venta_edit: pv, margen_edit: m, impuesto_edit: 18 };
      }));
      toast.success("Comparación lista");
    } catch { toast.error("Error de conexión — intentá de nuevo"); }
    finally  { setCargando(false); }
  };

  const recalcularFila = (idx, campo, val) => {
    setFilas((prev) => {
      const copia = [...prev];
      const f = { ...copia[idx] };
      if (campo === "precio_venta_edit") {
        f.precio_venta_edit = val;
        const margen = margenDesdeVenta(f.precio_base, val);
        if (margen != null) f.margen_edit = margen;
      } else if (campo === "margen_edit") {
        f.margen_edit = val;
        const pv = ventaDesdeMargen(f.precio_base, val);
        if (pv != null) f.precio_venta_edit = pv;
      } else if (campo === "precio_base") {
        f.precio_base = val;
        const pv = ventaDesdeMargen(val, f.margen_edit);
        if (pv != null) f.precio_venta_edit = pv;
      } else if (campo === "impuesto_edit") {
        f.impuesto_edit = val;
      }
      copia[idx] = f;
      return copia;
    });
  };

  // Símbolo y tipo de cambio activos
  const sym = moneda === "USD" ? "$" : "S/";
  const tc  = parseFloat(tipoCambio) || 0;

  // ── Paso 4: agrupación por estado (chips de filtro) y datos de preview ──
  const conteoEstados = useMemo(() => ({
    todos:            itemsVerif.length,
    encontrado:       itemsVerif.filter(i => i.estado === "encontrado" || i.estado === "creado").length,
    revisar:          itemsVerif.filter(i => i.estado === "revisar").length,
    nuevo:            itemsVerif.filter(i => i.estado === "nuevo").length,
    pendiente_manual: itemsVerif.filter(i => i.estado === "pendiente_manual").length,
  }), [itemsVerif]);

  const itemsFiltrados = useMemo(() =>
    itemsVerif
      .map((item, i) => ({ item, i }))
      .filter(({ item }) => {
        if (filtroEstado === "todos") return true;
        if (filtroEstado === "encontrado") return item.estado === "encontrado" || item.estado === "creado";
        return item.estado === filtroEstado;
      }),
    [itemsVerif, filtroEstado]);

  const hayExistentesPreview = itemsVerif.some(i => i.estado === "encontrado" || i.estado === "revisar");
  const previewData = useMemo(() => construirLineasPreview(itemsVerif, previewFuente), [itemsVerif, previewFuente]);
  const previewSubtotal = previewData.incluidas.reduce((s, l) => s + l.subtotal, 0);
  const previewIgv      = previewData.incluidas.reduce((s, l) => s + l.igv, 0);
  const previewTotal    = previewSubtotal + previewIgv;

  // Todos los precios en filas son SIN IGV — buyer agrega IGV al generar cotización
  const totalVenta       = filas.reduce((s, f) => s + (parseFloat(f.precio_venta_edit) || 0) * (f.cantidad || 1), 0);
  const totalCosto       = filas.reduce((s, f) => s + (parseFloat(f.precio_base) || 0) * (f.cantidad || 1), 0);
  const totalIGVVenta    = filas.reduce((s, f) => {
    const igvPct = parseFloat(f.impuesto_edit) || 0;
    return s + (parseFloat(f.precio_venta_edit) || 0) * (f.cantidad || 1) * (igvPct / 100);
  }, 0);
  const totalIGVCosto    = filas.reduce((s, f) => {
    const igvPct = parseFloat(f.impuesto_edit) || 0;
    return s + (parseFloat(f.precio_base) || 0) * (f.cantidad || 1) * (igvPct / 100);
  }, 0);
  const totalConIGV      = totalVenta + totalIGVVenta;
  const totalCostoConIGV = totalCosto + totalIGVCosto;
  // Flete se ingresa siempre en la moneda seleccionada
  const flete            = parseFloat(fleteTotal) || 0;
  const sunat            = totalConIGV * 0.01;      // 1% impuesto a la renta sobre total venta CON IGV
  const ganBruta         = totalConIGV - totalCostoConIGV;
  const ganNeta          = ganBruta - flete - sunat;
  const margenNetoP      = totalConIGV > 0 ? (ganNeta / totalConIGV * 100).toFixed(1) : 0;
  const semaforo         = parseFloat(margenNetoP) >= 12 ? GREEN : parseFloat(margenNetoP) >= 7 ? AMBER : RED;

  // Equivalentes en PEN cuando moneda = USD y hay tipo de cambio
  const penEq = (val) => (moneda === "USD" && tc > 0)
    ? <div style={{ fontSize: 10, color: GRAY, marginTop: 1 }}>≈ S/ {(val * tc).toFixed(2)}</div>
    : null;

  const verificarProductos = async () => {
    if (!clienteSel) { toast.error("Selecciona un cliente primero"); return; }
    setVerificando(true);
    try {
      const factor = preciosConIGVBuyer ? (1 / 1.18) : 1;
      const items = [];
      const elegibles = []; // { idx, f } — ítems con precio que sí entran al matching con IA

      filas.forEach((f, idx) => {
        if (!f.encontrado || !f.precio_base) {
          items[idx] = { ...f, estado: "pendiente_manual", buyer_id: null, buyer_desc: null, candidatos: [],
            pb_edit: String(parseFloat((parseFloat(f.precio_base || 0) * factor).toFixed(4)) || ""),
            pv_edit: String(parseFloat((parseFloat(f.precio_venta_edit || 0) * factor).toFixed(4)) || ""),
            pb_original: null, pv_original: null,
            confianza: null, razonamiento: null, modo: null,
            precio_actualizado: false };
        } else {
          elegibles.push({ idx, f });
        }
      });

      let modoGlobal = null;
      if (elegibles.length > 0) {
        const resp = await api({
          accion: "match_productos",
          items: elegibles.map(({ idx, f }) => ({ id: String(idx), descripcion: f.descripcion, categoria: f.categoria })),
        });
        if (!resp?.success) throw new Error(resp?.error || "Error en match_productos");
        modoGlobal = resp.modo_global;

        const porId = new Map((resp.resultados || []).map(r => [r.item_id, r]));
        elegibles.forEach(({ idx, f }) => {
          const r = porId.get(String(idx));
          const candidatoElegido = r?.buyer_id ? (r.candidatos || []).find(c => c.id === r.buyer_id) : null;

          items[idx] = {
            ...f,
            estado: r?.estado || "nuevo",
            buyer_id: r?.buyer_id || null,
            buyer_desc: r?.buyer_desc || null,
            candidatos: r?.candidatos || [],
            confianza: r?.confianza || null,
            razonamiento: r?.razonamiento || null,
            modo: r?.modo || null,
            // pb_edit/pv_edit: siempre en formato sin IGV (listo para buyer)
            pb_edit: candidatoElegido
              ? String(candidatoElegido.precio_base ?? "")
              : String(parseFloat((parseFloat(f.precio_base || 0) * factor).toFixed(4)) || ""),
            pv_edit: candidatoElegido
              ? String(candidatoElegido.precio_venta ?? "")
              : String(parseFloat((parseFloat(f.precio_venta_edit || 0) * factor).toFixed(4)) || ""),
            // Guarda el precio original de buyer para detectar si el usuario realmente lo cambió
            pb_original: candidatoElegido ? String(candidatoElegido.precio_base ?? "") : null,
            pv_original: candidatoElegido ? String(candidatoElegido.precio_venta ?? "") : null,
            precio_actualizado: false,
          };
        });
      }

      setModoGlobalMatch(modoGlobal);
      setItemsVerif(items);
      setPaso(4);
    } catch (e) {
      console.error("verificarProductos error:", e);
      toast.error("Error de conexión al verificar productos");
    }
    finally  { setVerificando(false); }
  };

  const cambiarBuyerSel = (idx, valor) => {
    setItemsVerif(prev => {
      const copia = [...prev];
      const item  = copia[idx];
      const factor = preciosConIGVBuyer ? (1 / 1.18) : 1;
      if (valor === "nuevo") {
        copia[idx] = { ...item, estado: "nuevo", buyer_id: null, buyer_desc: null,
          pb_edit: String(parseFloat((parseFloat(item.precio_base || 0) * factor).toFixed(4)) || ""),
          pv_edit: String(parseFloat((parseFloat(item.precio_venta_edit || 0) * factor).toFixed(4)) || ""),
          pb_original: null, pv_original: null,
          confianza: null, razonamiento: "Creado manualmente por el vendedor.", modo: "manual",
          precio_actualizado: false };
      } else {
        const cand = item.candidatos.find(c => String(c.id) === String(valor));
        if (cand) copia[idx] = { ...item, estado: "encontrado", buyer_id: cand.id, buyer_desc: cand.descripcion,
          pb_edit: String(cand.precio_base ?? ""), pv_edit: String(cand.precio_venta ?? ""),
          pb_original: String(cand.precio_base ?? ""), pv_original: String(cand.precio_venta ?? ""),
          confianza: null, razonamiento: "Seleccionado manualmente por el vendedor.", modo: "manual",
          precio_actualizado: false };
      }
      return copia;
    });
  };

  const actualizarPrecioBuyer = async (idx) => {
    const item = itemsVerif[idx];
    if (!item.buyer_id) return;
    const resp = await api({ accion: "actualizar_producto", id: item.buyer_id,
      precio_base: parseFloat(item.pb_edit) || 0, precio_venta: parseFloat(item.pv_edit) || 0 });
    if (resp.success) {
      setItemsVerif(prev => { const c = [...prev]; c[idx] = { ...c[idx], precio_actualizado: true }; return c; });
      toast.success("Precio actualizado en buyer permanentemente");
    } else {
      toast.error("Error al actualizar precio en buyer");
    }
  };

  const iniciarCrearCotizacion = () => {
    // Edge case del spec: una sugerencia de confianza media/baja ("revisar") nunca se
    // autoacepta — si el vendedor no la resolvió, se le pregunta explícitamente.
    const sinResolver = itemsVerif.filter(i => i.estado === "revisar");
    if (sinResolver.length > 0) {
      const ok = window.confirm(
        `Tienes ${sinResolver.length} producto(s) marcados como "Revisar" (sugerencia de IA con confianza media/baja) sin confirmar.\n\n¿Continuar usando esas sugerencias tentativas igual?`
      );
      if (!ok) return;
    }
    const hayExistentes = itemsVerif.some(i => i.estado === "encontrado" || i.estado === "revisar");
    if (hayExistentes) {
      setShowPrecioModal(true);
    } else {
      crearCotizacion("pipeline");
    }
  };

  const crearCotizacion = async (fuente = "pipeline") => {
    setCargando(true);
    try {
      const productosFinales = [];
      for (let i = 0; i < itemsVerif.length; i++) {
        const item = itemsVerif[i];
        let bid = item.buyer_id;

        // Para productos existentes en buyer, elegir fuente de precios (misma lógica que el preview)
        const { pb, pv } = calcularPrecioFinal(item, fuente);

        if (item.estado === "nuevo" && !bid) {
          const descLimpia = limpiarDescripcion(item.descripcion_proveedor || item.descripcion);
          const resp = await api({
            accion: "crear_producto",
            proveedor_id:  proveedorSel?.id || null,
            descripcion:   descLimpia,
            precio_base:   parseFloat(item.pb_edit) || 0,
            precio_venta:  parseFloat(item.pv_edit) || 0,
            unidad_medida: item.unidad || "UND",
            categoria:     item.categoria || null,
            moneda,
          });
          if (resp.success) {
            bid = resp.data.id;
            setItemsVerif((prev) => { const c = [...prev]; c[i] = { ...c[i], buyer_id: bid, estado: "creado" }; return c; });
            // Advertencia de posible duplicado -- no bloquea, el producto ya se creó
            if (resp.advertencia_duplicado) {
              toast(resp.advertencia_duplicado, { icon: "⚠️" });
            }
          } else { toast.error(`No se pudo crear: ${item.descripcion}`); return; }
        }
        if (bid) productosFinales.push({
          producto_id:            bid,
          cantidad:               item.cantidad || 1,
          precio_unitario:        pv,
          precio_base_cotizacion: pb,
          margen_porcentaje:      parseFloat(item.margen_edit) || null,
          impuesto:               parseFloat(item.impuesto_edit ?? 18),
        });
      }
      if (!productosFinales.length) { toast.error("No hay productos válidos"); return; }
      const resp = await api({
        accion: "crear_cotizacion",
        cliente_id: clienteSel.id,
        observaciones: obsGlobal || null,
        moneda,
        tipo_cambio: moneda === "USD" && tc > 0 ? tc : null,
        productos: productosFinales,
      });
      if (resp.success) {
        setResultado(resp);
        // Si se usaron precios del pipeline, actualizarlos en buyer para cotizaciones futuras
        // (no afecta cotizaciones ya creadas: sus precios están fijos en cotizacion_productos)
        if (fuente === "pipeline") {
          const existentes = itemsVerif.filter(i => (i.estado === "encontrado" || i.estado === "revisar") && i.buyer_id);
          if (existentes.length > 0) {
            const updates = await Promise.allSettled(
              existentes.map(item => api({
                accion: "actualizar_producto",
                id:          item.buyer_id,
                precio_base: parseFloat(item.precio_base) || parseFloat(item.pb_edit) || 0,
                precio_venta: parseFloat(item.precio_venta_edit) > 0
                  ? parseFloat(item.precio_venta_edit)
                  : (parseFloat(item.pv_edit) || 0),
              }))
            );
            const ok = updates.filter(r => r.status === "fulfilled" && r.value?.success).length;
            toast.success(`Cotización creada · ${ok}/${existentes.length} precios actualizados en buyer`);
          } else {
            toast.success("Cotización creada en buyer");
          }
        } else {
          toast.success("Cotización creada en buyer");
        }
      }
      else toast.error("Error API: " + (resp.message || JSON.stringify(resp).slice(0, 120)));
    } catch (e) { console.error("crearCotizacion error:", e); toast.error("Error: " + (e?.message || "conexión")); }
    finally  { setCargando(false); }
  };

  const onImagen = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagenMime(file.type);
    const reader = new FileReader();
    reader.onload = (ev) => setImagenB64(ev.target.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  const resetear = () => {
    localStorage.removeItem(DRAFT_KEY);
    borradorRef.current = {};
    setPaso(1); setProductos([]); setComparacion([]); setFilas([]);
    setItemsVerif([]); setResultado(null); setClienteSel(null); setClienteQ("");
    setTextoReq(""); setTextoProvee(""); setFleteTotal(""); setObsGlobal("");
    setImagenB64(null); setArchivoReq(null); setArchivoProvee(null); setImagenProveeB64(null);
    setProveedorConIGV(false); setPreciosConIGVBuyer(false); setInputTipo("texto"); setInputTipo2("texto");
    setMoneda("PEN"); setTipoCambio("");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F0F2FA" }}>

      {/* ── Header gradiente ── */}
      <div style={{
        background: "linear-gradient(135deg, #1E3A5F 0%, #3A3C7A 100%)",
        boxShadow: "0 4px 20px rgba(30,58,95,0.18)",
        padding: "22px 28px 20px",
        marginBottom: 0,
      }}>
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <h2 style={{ margin: 0, fontSize: 22, color: "#fff", fontWeight: 800, letterSpacing: "-0.3px" }}>
            ✨ Pipeline de Cotización
          </h2>
          <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.65)", fontSize: 13 }}>
            Organiza con IA, compara precios del proveedor y crea la cotización en buyer automáticamente.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: "0 auto", padding: "24px 20px" }}>

        {/* ── Banner borrador restaurado ── */}
        {hayBorrador && !resultado && (
          <div style={{
            background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10,
            padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center",
            gap: 10, fontSize: 13,
          }}>
            <span>📋</span>
            <span style={{ flex: 1, color: "#92400E", fontWeight: 500 }}>
              Borrador restaurado — continuás desde donde lo dejaste.
            </span>
            <button onClick={resetear} style={{
              background: "none", border: "1px solid #F59E0B", borderRadius: 7,
              padding: "3px 10px", fontSize: 12, color: "#92400E", cursor: "pointer", fontWeight: 600,
            }}>Empezar de cero</button>
          </div>
        )}

        {/* ── Selector de cliente ── */}
        <div className="card-lift" style={{
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
          padding: "16px 20px", marginBottom: 20, position: "relative",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}>
          <SectionTitle color={NAV} label="Cliente" />
          {clienteSel ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #3A3C7A, #7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{(clienteSel.empresa || clienteSel.nombre || "?")[0]}</span>
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, color: NAV, fontSize: 15 }}>{clienteSel.empresa || clienteSel.nombre}</span>
                {clienteSel.ruc && <span style={{ color: GRAY, fontSize: 12, marginLeft: 8 }}>RUC {clienteSel.ruc}</span>}
              </div>
              <button onClick={() => { setClienteSel(null); setClienteQ(""); }}
                style={{ background: "none", border: "none", color: GRAY, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "2px 6px" }}>×</button>
            </div>
          ) : (
            <>
              <input value={clienteQ} onChange={(e) => buscarClientes(e.target.value)}
                placeholder="Buscar cliente por empresa, nombre o RUC..."
                style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "9px 13px", fontSize: 14, outline: "none", boxSizing: "border-box", transition: "border-color 150ms" }}
                onFocus={(e) => e.target.style.borderColor = NAV}
                onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
              />
              {buscandoCli && <div style={{ fontSize: 12, color: GRAY, marginTop: 4 }}>Buscando...</div>}
              {clientesRes.length > 0 && (
                <div style={{ position: "absolute", left: 20, right: 20, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, zIndex: 99, boxShadow: "0 8px 24px rgba(0,0,0,.12)", marginTop: 4 }}>
                  {clientesRes.map((c) => (
                    <div key={c.id} onClick={() => { setClienteSel(c); setClienteQ(""); setClientesRes([]); }}
                      style={{ padding: "11px 16px", cursor: "pointer", borderBottom: "1px solid #f3f4f6", fontSize: 14, transition: "background 120ms" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = LIGHTBG}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                    >
                      <span style={{ fontWeight: 600 }}>{c.empresa || c.nombre}</span>
                      {c.ruc && <span style={{ color: GRAY, fontSize: 12, marginLeft: 8 }}>RUC {c.ruc}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Tabs de pasos ── */}
        <div style={{ display: "flex", gap: 0, marginBottom: 20, background: "#e8eaf6", borderRadius: 12, padding: 5 }}>
          {PASOS.map((p) => {
            const activo = paso === p.n;
            const hecho  = paso > p.n;
            return (
              <button key={p.n} onClick={() => hecho && setPaso(p.n)}
                style={{
                  flex: 1, padding: "10px 6px", border: "none", borderRadius: 9,
                  cursor: hecho ? "pointer" : "default",
                  background: activo ? NAV : "transparent",
                  color: activo ? "#fff" : hecho ? GREEN : GRAY,
                  fontWeight: activo ? 700 : 500, fontSize: 12,
                  transition: "all 250ms var(--ease-out)",
                  boxShadow: activo ? "0 2px 8px rgba(58,60,122,0.3)" : "none",
                }}
              >
                <span style={{ display: "block", fontSize: activo ? 17 : 15, lineHeight: 1, marginBottom: 3, transition: "font-size 200ms" }}>
                  {hecho ? "✓" : p.n}
                </span>
                {p.label}
              </button>
            );
          })}
        </div>

        {/* ── PASO 1 ── */}
        {paso === 1 && (
          <div key="paso1" className="card-lift" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 22, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", animation: "slideUp 280ms var(--ease-out)" }}>
            <SectionTitle color={NAV} label="Requerimiento del cliente" />

            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { id: "texto",   icon: "📝", label: "Texto"         },
                { id: "imagen",  icon: "📷", label: "Foto / imagen" },
                { id: "archivo", icon: "📎", label: "Archivo"       },
              ].map((t) => (
                <button key={t.id} onClick={() => setInputTipo(t.id)}
                  style={{
                    padding: "7px 18px", borderRadius: 20, fontWeight: 600, fontSize: 13, cursor: "pointer",
                    border: `2px solid ${inputTipo === t.id ? NAV : "#e5e7eb"}`,
                    background: inputTipo === t.id ? NAV : "#fff",
                    color: inputTipo === t.id ? "#fff" : GRAY,
                    transition: "all 200ms var(--ease-out)",
                  }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {inputTipo === "texto" && (
              <textarea value={textoReq} onChange={(e) => setTextoReq(e.target.value)} rows={7}
                placeholder={"Pega el requerimiento del cliente (WhatsApp, email, voz transcrita)...\n\nEjemplo:\n- 50 fierros 1/2\"\n- 2 toneladas cemento sol\n- 3000 ladrillos King Kong 18H"}
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 9, padding: "11px 13px", fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", transition: "border-color 150ms" }}
                onFocus={(e) => e.target.style.borderColor = NAV}
                onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
              />
            )}
            {inputTipo === "imagen" && (
              <div onClick={() => fileRef.current.click()}
                style={{ border: `2px dashed ${imagenB64 ? GREEN : "#c7d2fe"}`, borderRadius: 12, padding: "34px 20px", textAlign: "center", cursor: "pointer", color: imagenB64 ? GREEN : GRAY, background: imagenB64 ? "#f0fdf8" : LIGHTBG, transition: "all 200ms" }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{imagenB64 ? "✅" : "📷"}</div>
                {imagenB64 ? "Imagen cargada — clic para cambiar" : "Clic para subir foto del requerimiento"}
              </div>
            )}
            {inputTipo === "archivo" && (
              <div onClick={() => archivoRef.current.click()}
                style={{ border: `2px dashed ${archivoReq ? GREEN : "#c7d2fe"}`, borderRadius: 12, padding: "34px 20px", textAlign: "center", cursor: "pointer", color: archivoReq ? GREEN : GRAY, background: archivoReq ? "#f0fdf8" : LIGHTBG, transition: "all 200ms" }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{archivoReq ? "✅" : "📎"}</div>
                {archivoReq
                  ? <><strong>{archivoReq.nombre}</strong><br /><span style={{ fontSize: 12 }}>Clic para cambiar</span></>
                  : <><strong>Clic para subir archivo</strong><br /><span style={{ fontSize: 12 }}>PDF, Excel (.xlsx) o Word (.docx)</span></>
                }
              </div>
            )}
            <input ref={fileRef}     type="file" accept="image/*" style={{ display: "none" }} onChange={onImagen} />
            <input ref={archivoRef}  type="file" accept=".pdf,.xlsx,.xls,.docx,.doc" style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                try {
                  const parsed = await leerArchivo(file);
                  setArchivoReq(parsed);
                  toast.success(`${file.name} listo`);
                } catch (err) { toast.error(err.message || "Error al leer el archivo"); }
              }} />

            <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
              {cargando ? <Cargando texto="Organizando con IA..." /> : (
                <BtnPrimary onClick={organizar}>✨ Organizar con IA</BtnPrimary>
              )}
            </div>

            {productos.length > 0 && (
              <div style={{ marginTop: 22 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <SectionTitle color={GREEN} label="Productos organizados" count={productos.length} />
                  <button onClick={copiarParaProveedor}
                    style={{ background: "none", border: `1px solid ${NAV}`, borderRadius: 8, padding: "5px 14px", color: NAV, fontWeight: 600, fontSize: 12, cursor: "pointer", flexShrink: 0, marginLeft: 12 }}>
                    📋 Copiar para proveedor
                  </button>
                </div>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: LIGHTBG }}>
                        {["Categoría", "Descripción", "Cant.", "Unidad", "Notas"].map((h) => (
                          <th key={h} style={{ padding: "9px 11px", textAlign: "left", color: GRAY, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {productos.map((p, i) => (
                        <tr key={i} className="stagger-item" style={{ borderTop: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "8px 11px" }}>
                            <span style={{ background: "#eef0fa", color: NAV, borderRadius: 12, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{p.categoria}</span>
                          </td>
                          <td style={{ padding: "8px 11px" }}>
                            <input value={p.descripcion} onChange={(e) => { const c = [...productos]; c[i] = { ...c[i], descripcion: e.target.value }; setProductos(c); }}
                              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px", width: "100%", fontSize: 13, outline: "none" }} />
                          </td>
                          <td style={{ padding: "8px 11px", width: 75 }}>
                            <input type="number" value={p.cantidad ?? ""} onChange={(e) => { const c = [...productos]; c[i] = { ...c[i], cantidad: e.target.value }; setProductos(c); }}
                              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 6px", width: 60, fontSize: 13, outline: "none" }} />
                          </td>
                          <td style={{ padding: "8px 11px", color: GRAY }}>{p.unidad}</td>
                          <td style={{ padding: "8px 11px", color: GRAY, fontSize: 12 }}>{p.notas}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                  <BtnPrimary onClick={() => setPaso(2)} color={GREEN}>Siguiente → Paso 2</BtnPrimary>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── PASO 2 ── */}
        {paso === 2 && (
          <div key="paso2" className="card-lift" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 22, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", animation: "slideUp 280ms var(--ease-out)" }}>
            <SectionTitle color={NAV} label="Respuesta del proveedor" />
            <p style={{ margin: "-6px 0 14px", color: GRAY, fontSize: 13 }}>Pega el texto o sube el archivo de la cotización del proveedor</p>

            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                { id: "texto",   icon: "📝", label: "Texto"         },
                { id: "imagen",  icon: "📷", label: "Foto / imagen" },
                { id: "archivo", icon: "📎", label: "Archivo"       },
              ].map((t) => (
                <button key={t.id} onClick={() => setInputTipo2(t.id)}
                  style={{
                    padding: "7px 18px", borderRadius: 20, fontWeight: 600, fontSize: 13, cursor: "pointer",
                    border: `2px solid ${inputTipo2 === t.id ? NAV : "#e5e7eb"}`,
                    background: inputTipo2 === t.id ? NAV : "#fff",
                    color: inputTipo2 === t.id ? "#fff" : GRAY,
                    transition: "all 200ms var(--ease-out)",
                  }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {inputTipo2 === "texto" && (
              <textarea value={textoProvee} onChange={(e) => setTextoProvee(e.target.value)} rows={8}
                placeholder={"Pega aquí la respuesta del proveedor...\n\nEjemplo:\nFIERRO CORRUGADO 1/2 X 9M AA  S/ 28.50 c/u\nCEMENTO SOL BOLSA 42.5 KG  S/ 28.00\nLADRILLO KING KONG 18H  S/ 0.90 c/u"}
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 9, padding: "11px 13px", fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", transition: "border-color 150ms" }}
                onFocus={(e) => e.target.style.borderColor = NAV}
                onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
              />
            )}
            {inputTipo2 === "imagen" && (
              <div onClick={() => imagenRef2.current.click()}
                style={{ border: `2px dashed ${imagenProveeB64 ? GREEN : "#c7d2fe"}`, borderRadius: 12, padding: "34px 20px", textAlign: "center", cursor: "pointer", color: imagenProveeB64 ? GREEN : GRAY, background: imagenProveeB64 ? "#f0fdf8" : LIGHTBG, transition: "all 200ms" }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{imagenProveeB64 ? "✅" : "📷"}</div>
                {imagenProveeB64 ? "Imagen cargada — clic para cambiar" : "Clic para subir foto / captura de pantalla de la cotización"}
              </div>
            )}
            {inputTipo2 === "archivo" && (
              <div onClick={() => archivoRef2.current.click()}
                style={{ border: `2px dashed ${archivoProvee ? GREEN : "#c7d2fe"}`, borderRadius: 12, padding: "34px 20px", textAlign: "center", cursor: "pointer", color: archivoProvee ? GREEN : GRAY, background: archivoProvee ? "#f0fdf8" : LIGHTBG, transition: "all 200ms" }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{archivoProvee ? "✅" : "📎"}</div>
                {archivoProvee
                  ? <><strong>{archivoProvee.nombre}</strong><br /><span style={{ fontSize: 12 }}>Clic para cambiar</span></>
                  : <><strong>Clic para subir cotización del proveedor</strong><br /><span style={{ fontSize: 12 }}>PDF, Excel (.xlsx) o Word (.docx)</span></>
                }
              </div>
            )}
            <input ref={imagenRef2}  type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                setImagenProveeMime(file.type);
                const reader = new FileReader();
                reader.onload = (ev) => setImagenProveeB64(ev.target.result.split(",")[1]);
                reader.readAsDataURL(file);
              }} />
            <input ref={archivoRef2} type="file" accept=".pdf,.xlsx,.xls,.docx,.doc" style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                try {
                  const parsed = await leerArchivo(file);
                  setArchivoProvee(parsed);
                  toast.success(`${file.name} listo`);
                } catch (err) { toast.error(err.message || "Error al leer el archivo"); }
              }} />
            {/* Toggle IGV proveedor — fuente de verdad manual */}
            <div style={{ marginTop: 14, padding: "12px 16px", background: "#F8F9FF", borderRadius: 10, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                ¿Los precios de esta cotización incluyen IGV (18%)?
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {[
                  { val: false, label: "No — Sin IGV", sub: "precio neto, buyer agrega 18%" },
                  { val: true,  label: "Sí — Con IGV",  sub: "precios se ajustarán ÷1.18" },
                ].map(({ val, label, sub }) => {
                  const activo = proveedorConIGV === val;
                  return (
                    <button key={String(val)} onClick={() => setProveedorConIGV(val)}
                      style={{
                        padding: "7px 18px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
                        border: `2px solid ${activo ? (val ? AMBER : GREEN) : "#e5e7eb"}`,
                        background: activo ? (val ? "#FEF3C7" : "#F0FDF4") : "#fff",
                        color: activo ? (val ? "#92400E" : "#166534") : GRAY,
                        transition: "all 160ms var(--ease-out)",
                      }}>
                      {label}
                      <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2 }}>{sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Identidad del proveedor — para asignarla a los productos nuevos que se creen */}
            <div style={{ marginTop: 10, padding: "12px 16px", background: "#F8F9FF", borderRadius: 10, border: "1px solid #e5e7eb", position: "relative" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                ¿A qué proveedor le compras esto?
              </div>
              {proveedorSel ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, color: NAV, fontSize: 14 }}>{proveedorSel.empresa || proveedorSel.nombre}</span>
                    {proveedorSel.ruc && <span style={{ color: GRAY, fontSize: 12, marginLeft: 8 }}>RUC {proveedorSel.ruc}</span>}
                  </div>
                  <button onClick={() => setProveedorSel(null)}
                    style={{ background: "none", border: "none", color: GRAY, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "2px 6px" }}>×</button>
                </div>
              ) : creandoProveedor ? (
                <div>
                  <input value={nuevoProvNombre} onChange={(e) => setNuevoProvNombre(e.target.value)}
                    placeholder="Nombre del proveedor o contacto *"
                    style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 6, boxSizing: "border-box", outline: "none" }} />
                  <input value={nuevoProvEmpresa} onChange={(e) => setNuevoProvEmpresa(e.target.value)}
                    placeholder="Empresa (opcional)"
                    style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 6, boxSizing: "border-box", outline: "none" }} />
                  <input value={nuevoProvRuc} onChange={(e) => setNuevoProvRuc(e.target.value)}
                    placeholder="RUC (opcional)"
                    style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 8, boxSizing: "border-box", outline: "none" }} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={crearProveedorNuevo} disabled={guardandoProveedor}
                      style={{ background: NAV, color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontWeight: 700, fontSize: 13, cursor: guardandoProveedor ? "not-allowed" : "pointer" }}>
                      {guardandoProveedor ? "Guardando..." : "Guardar proveedor"}
                    </button>
                    <button onClick={() => { setCreandoProveedor(false); setNuevoProvNombre(""); setNuevoProvEmpresa(""); setNuevoProvRuc(""); }}
                      style={{ background: "#f3f4f6", color: GRAY, border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <input value={proveedorQ} onChange={(e) => buscarProveedores(e.target.value)}
                    placeholder="Buscar proveedor por nombre, empresa o RUC..."
                    style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", transition: "border-color 150ms" }}
                    onFocus={(e) => e.target.style.borderColor = NAV}
                    onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                  />
                  {buscandoProv && <div style={{ fontSize: 12, color: GRAY, marginTop: 4 }}>Buscando...</div>}
                  {proveedorQ.length >= 2 && !buscandoProv && (
                    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, marginTop: 6, boxShadow: "0 4px 14px rgba(0,0,0,.08)" }}>
                      {proveedoresRes.map((p) => (
                        <div key={p.id} onClick={() => { setProveedorSel(p); setProveedorQ(""); setProveedoresRes([]); }}
                          style={{ padding: "9px 14px", cursor: "pointer", borderBottom: "1px solid #f3f4f6", fontSize: 13, transition: "background 120ms" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = LIGHTBG}
                          onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                        >
                          <span style={{ fontWeight: 600 }}>{p.empresa || p.nombre}</span>
                          {p.ruc && <span style={{ color: GRAY, fontSize: 11, marginLeft: 8 }}>RUC {p.ruc}</span>}
                        </div>
                      ))}
                      <div onClick={() => { setCreandoProveedor(true); setNuevoProvNombre(proveedorQ); }}
                        style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: NAV, transition: "background 120ms" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = LIGHTBG}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                      >
                        + No está en la lista — crear proveedor nuevo
                      </div>
                    </div>
                  )}
                </>
              )}
              {!proveedorSel && !creandoProveedor && (
                <div style={{ fontSize: 11, color: AMBER, marginTop: 6, fontWeight: 600 }}>
                  ⚠ Sin proveedor, los productos nuevos que crees acá quedarán sin ese dato asignado.
                </div>
              )}
            </div>

            {/* Toggle moneda de la cotización */}
            <div style={{ marginTop: 10, padding: "12px 16px", background: "#F0F9FF", borderRadius: 10, border: "1px solid #BAE6FD" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0C4A6E", marginBottom: 8 }}>
                ¿En qué moneda es esta cotización?
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {[
                  { val: "PEN", label: "S/ Soles (PEN)",   sub: "moneda local"    },
                  { val: "USD", label: "$ Dólares (USD)",  sub: "tipo de cambio necesario" },
                ].map(({ val, label, sub }) => {
                  const activo = moneda === val;
                  return (
                    <button key={val} onClick={() => setMoneda(val)}
                      style={{
                        padding: "7px 18px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
                        border: `2px solid ${activo ? "#0284C7" : "#e5e7eb"}`,
                        background: activo ? "#E0F2FE" : "#fff",
                        color: activo ? "#0C4A6E" : GRAY,
                        transition: "all 160ms var(--ease-out)",
                      }}>
                      {label}
                      <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2 }}>{sub}</div>
                    </button>
                  );
                })}
                {moneda === "USD" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#0C4A6E" }}>Tipo de cambio S/ por $</span>
                    <input
                      type="number"
                      value={tipoCambio}
                      onChange={(e) => setTipoCambio(e.target.value)}
                      placeholder="3.75"
                      style={{ border: "2px solid #0284C7", borderRadius: 7, padding: "5px 9px", fontSize: 14, width: 80, outline: "none", fontWeight: 700, color: "#0C4A6E" }}
                    />
                  </div>
                )}
              </div>
              {moneda === "USD" && !tipoCambio && (
                <div style={{ fontSize: 11, color: AMBER, marginTop: 5, fontWeight: 600 }}>
                  ⚠ Ingresa el tipo de cambio para ver equivalentes en soles en los KPIs
                </div>
              )}
            </div>

            <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
              <BtnSecondary onClick={() => setPaso(1)}>← Volver</BtnSecondary>
              {cargando ? <Cargando texto="Comparando con IA..." /> : (
                <BtnPrimary onClick={comparar}>✨ Comparar con IA</BtnPrimary>
              )}
            </div>

            {comparacion.length > 0 && (
              <div style={{ marginTop: 22 }}>
                <SectionTitle color={GREEN}
                  label={`${comparacion.filter(c => c.encontrado).length}/${comparacion.length} encontrados`}
                />
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: LIGHTBG }}>
                        {["", "Requerimiento", "Proveedor", "P. Base", "Margen", "P. Venta"].map((h) => (
                          <th key={h} style={{ padding: "9px 11px", textAlign: "left", color: GRAY, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparacion.map((c, i) => (
                        <tr key={i} className="stagger-item" style={{ borderTop: "1px solid #f3f4f6", background: c.encontrado ? "#fff" : "#fff9f9" }}>
                          <td style={{ padding: "8px 11px", textAlign: "center", fontSize: 16 }}>{c.encontrado ? "✅" : "❌"}</td>
                          <td style={{ padding: "8px 11px" }}>
                            <span style={{ fontWeight: 600, display: "block" }}>{c.descripcion}</span>
                            <span style={{ color: GRAY, fontSize: 11 }}>{c.cantidad} {c.unidad}</span>
                          </td>
                          <td style={{ padding: "8px 11px", color: GRAY, fontSize: 12 }}>{c.descripcion_proveedor || "—"}</td>
                          <td style={{ padding: "8px 11px" }}>{c.precio_base ? `${moneda === "USD" ? "$" : "S/"} ${c.precio_base}` : "—"}</td>
                          <td style={{ padding: "8px 11px" }}>{c.margen_sugerido ? <ChipMargen pct={c.margen_sugerido} /> : "—"}</td>
                          <td style={{ padding: "8px 11px", fontWeight: 700, color: GREEN }}>{c.precio_venta ? `${moneda === "USD" ? "$" : "S/"} ${c.precio_venta}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                  <BtnPrimary onClick={() => setPaso(3)} color={GREEN}>Siguiente → Paso 3</BtnPrimary>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── PASO 3 ── */}
        {paso === 3 && (
          <div key="paso3" style={{ animation: "slideUp 280ms var(--ease-out)" }}>
            <div className="card-lift" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 22, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <SectionTitle color={NAV} label="Análisis de márgenes" count={filas.length} />
                {proveedorConIGV && (
                  <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, border: "1px solid #FCD34D", flexShrink: 0 }}>
                    ⚠️ Proveedor cotizó CON IGV — precios ajustados ÷1.18
                  </span>
                )}
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 860 }}>
                  <thead>
                    <tr style={{ background: LIGHTBG }}>
                      {[`Producto`, `Cant.`, `P.Costo ${sym}IGV`, `P.Costo C/IGV`, `SubCosto`, `Margen%`, `P.Venta ${sym}IGV`, `P.Venta C/IGV`, `IGV`, `SubVenta`].map((h) => (
                        <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: GRAY, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: ".5px", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f, i) => {
                      const igvPct    = parseFloat(f.impuesto_edit) || 0;
                      const pb        = parseFloat(f.precio_base) || 0;
                      const pv        = parseFloat(f.precio_venta_edit) || 0;
                      const cant      = f.cantidad || 1;
                      const pbCigv    = pb * (1 + igvPct / 100);
                      const pvCigv    = pv * (1 + igvPct / 100);
                      const subCosto  = pb * cant;
                      const subVenta  = pv * cant;
                      return (
                        <tr key={i} className="stagger-item" style={{ borderTop: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "7px 10px", fontWeight: 600, maxWidth: 170 }}>
                            <span style={{ display: "block", fontSize: 12 }}>{f.descripcion}</span>
                            <span style={{ background: "#eef0fa", color: NAV, borderRadius: 10, padding: "1px 6px", fontSize: 9, fontWeight: 700 }}>{f.categoria}</span>
                          </td>
                          <td style={{ padding: "7px 10px", color: GRAY, whiteSpace: "nowrap", fontSize: 12 }}>{cant} {f.unidad}</td>
                          {/* P.Costo S/IGV — editable */}
                          <td style={{ padding: "7px 10px" }}>
                            <input type="number" value={f.precio_base ?? ""} onChange={(e) => recalcularFila(i, "precio_base", e.target.value)}
                              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "3px 5px", width: 68, fontSize: 12, outline: "none" }} />
                          </td>
                          {/* P.Costo C/IGV — calculado */}
                          <td style={{ padding: "7px 10px", color: GRAY, whiteSpace: "nowrap", fontSize: 12 }}>
                            {pb > 0 ? `${sym} ${pbCigv.toFixed(2)}` : "—"}
                          </td>
                          {/* SubCosto S/IGV */}
                          <td style={{ padding: "7px 10px", color: GRAY, whiteSpace: "nowrap", fontSize: 12 }}>
                            {pb > 0 ? `${sym} ${subCosto.toFixed(2)}` : "—"}
                          </td>
                          {/* Margen% — editable */}
                          <td style={{ padding: "7px 10px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <input type="number" value={f.margen_edit} onChange={(e) => recalcularFila(i, "margen_edit", e.target.value)}
                                style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "3px 4px", width: 42, fontSize: 12, outline: "none" }} />
                              <ChipMargen pct={parseFloat(f.margen_edit) || 0} />
                            </div>
                          </td>
                          {/* P.Venta S/IGV — editable */}
                          <td style={{ padding: "7px 10px" }}>
                            <input type="number" value={f.precio_venta_edit} onChange={(e) => recalcularFila(i, "precio_venta_edit", e.target.value)}
                              style={{ border: `1px solid ${NAV}40`, borderRadius: 6, padding: "3px 5px", width: 72, fontSize: 12, fontWeight: 700, outline: "none", color: NAV }} />
                          </td>
                          {/* P.Venta C/IGV — calculado */}
                          <td style={{ padding: "7px 10px", fontWeight: 600, color: NAV, whiteSpace: "nowrap", fontSize: 12 }}>
                            {pv > 0 ? `${sym} ${pvCigv.toFixed(2)}` : "—"}
                          </td>
                          {/* IGV toggle */}
                          <td style={{ padding: "7px 10px" }}>
                            <button onClick={() => recalcularFila(i, "impuesto_edit", f.impuesto_edit === 18 ? 0 : 18)}
                              style={{ padding: "2px 8px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 10, fontWeight: 700,
                                background: f.impuesto_edit === 18 ? "#FEF3C7" : "#F3F4F6",
                                color:      f.impuesto_edit === 18 ? "#92400E" : GRAY }}>
                              {f.impuesto_edit === 18 ? "18%" : "0%"}
                            </button>
                          </td>
                          {/* SubVenta */}
                          <td style={{ padding: "7px 10px", fontWeight: 700, color: NAV, whiteSpace: "nowrap", fontSize: 12 }}>
                            {sym} {subVenta.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Badge de moneda activa */}
            {moneda === "USD" && (
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ background: "#E0F2FE", color: "#0C4A6E", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, border: "1px solid #BAE6FD" }}>
                  $ Cotización en USD
                </span>
                {tc > 0 && (
                  <span style={{ color: GRAY, fontSize: 11 }}>
                    TC: S/ {tc.toFixed(4)} por $ — equivalentes en soles se muestran debajo de cada valor
                  </span>
                )}
                {!tc && (
                  <span style={{ color: AMBER, fontSize: 11, fontWeight: 600 }}>
                    ⚠ Sin tipo de cambio — volvé al paso 2 para ingresarlo
                  </span>
                )}
              </div>
            )}

            {/* KPIs financieros — Fila 1: totales */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 10 }}>
              {[
                { label: `Total Venta ${sym}IGV`, val: totalVenta,       color: NAV,  sub: null },
                { label: `Total Costo ${sym}IGV`, val: totalCosto,       color: GRAY, sub: null },
                { label: `Total Venta C/IGV`,     val: totalConIGV,      color: NAV,  sub: "lo que factura buyer" },
                { label: `Total Costo C/IGV`,     val: totalCostoConIGV, color: GRAY, sub: "lo que pagás al proveedor" },
              ].map(({ label, val, color, sub }) => (
                <div key={label} className="kpi-card" style={{ background: "#fff", border: `1px solid ${color}22`, borderRadius: 10, padding: "12px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color }}>{sym} {val.toFixed(2)}</div>
                  {penEq(val)}
                  {sub && <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{sub}</div>}
                </div>
              ))}
            </div>

            {/* KPIs financieros — Fila 2: ganancia */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
              {[
                { label: "Ganancia Bruta C/IGV",     val: ganBruta, color: ganBruta > 0 ? GREEN : RED, sub: `venta c/IGV − costo c/IGV` },
                { label: `SUNAT 1% (s/total c/IGV)`, val: sunat,    color: AMBER, sub: `${totalConIGV.toFixed(2)} × 1%` },
                { label: "Flete",                    val: flete,    color: AMBER, sub: "agencia transporte" },
                { label: "Ganancia Neta",            val: ganNeta,  color: semaforo, sub: `${margenNetoP}% sobre venta c/IGV` },
              ].map(({ label, val, color, sub }) => (
                <div key={label} className="kpi-card" style={{ background: "#fff", border: `1px solid ${color}30`, borderRadius: 10, padding: "12px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color }}>{sym} {val.toFixed(2)}</div>
                  {penEq(val)}
                  {sub && <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{sub}</div>}
                </div>
              ))}
            </div>

            {/* Semáforo margen neto */}
            <div className="kpi-card" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 11, padding: "14px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
              <div>
                <div style={{ fontSize: 11, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 4 }}>Margen neto final</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: semaforo }}>{margenNetoP}%</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: semaforo, boxShadow: `0 0 0 4px ${semaforo}30`, marginLeft: "auto", marginBottom: 4 }} />
                <span style={{ fontSize: 12, color: GRAY }}>{parseFloat(margenNetoP) >= 12 ? "Bueno" : parseFloat(margenNetoP) >= 7 ? "Ajustado" : "Bajo"}</span>
              </div>
            </div>

            {/* Flete + Observaciones */}
            <div className="card-lift" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div>
                  <SectionTitle color={AMBER} label="Flete total (agencia transporte)" />
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 700, color: GRAY }}>{sym}</span>
                    <input type="number" value={fleteTotal} onChange={(e) => setFleteTotal(e.target.value)} placeholder="0.00"
                      style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: "9px 11px", fontSize: 15, outline: "none", width: 130, transition: "border-color 150ms" }}
                      onFocus={(e) => e.target.style.borderColor = AMBER}
                      onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                    />
                  </div>
                  <p style={{ fontSize: 11, color: GRAY, margin: "5px 0 0" }}>Se resta de la ganancia bruta al calcular margen neto</p>
                </div>
                <div>
                  <SectionTitle color={GRAY} label="Observaciones para buyer" />
                  <textarea value={obsGlobal} onChange={(e) => setObsGlobal(e.target.value)} rows={3}
                    placeholder="Condiciones de pago, tiempo de entrega, notas al cliente..."
                    style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 8, padding: "9px 11px", fontSize: 13, resize: "none", outline: "none", fontFamily: "inherit", transition: "border-color 150ms" }}
                    onFocus={(e) => e.target.style.borderColor = NAV}
                    onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                  />
                </div>
              </div>
            </div>

            {/* Toggle precios sin/con IGV — se aplica al pasar a buyer */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "9px 14px", background: "#F8F9FF", borderRadius: 9, border: "1px solid #e5e7eb" }}>
              <span style={{ fontSize: 12, color: GRAY, fontWeight: 600, flexShrink: 0 }}>Los precios que ingresé son:</span>
              {[{ val: false, label: "Sin IGV" }, { val: true, label: "Con IGV" }].map(({ val, label }) => {
                const activo = preciosConIGVBuyer === val;
                return (
                  <button key={label} onClick={() => setPreciosConIGVBuyer(val)}
                    style={{ padding: "4px 12px", borderRadius: 16, fontWeight: 700, fontSize: 11, cursor: "pointer",
                      border: `2px solid ${activo ? NAV : "#e5e7eb"}`,
                      background: activo ? NAV : "#fff", color: activo ? "#fff" : GRAY }}>
                    {label}
                  </button>
                );
              })}
              <span style={{ fontSize: 10, color: GRAY }}>
                {preciosConIGVBuyer ? "→ se guardará ÷1.18 en buyer" : "→ se guardará tal cual en buyer"}
              </span>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <BtnSecondary onClick={() => setPaso(2)}>← Volver</BtnSecondary>
              {verificando
                ? <Cargando texto="Verificando en buyer..." />
                : <BtnPrimary onClick={verificarProductos} disabled={!clienteSel}>→ Verificar en buyer</BtnPrimary>
              }
            </div>
            {!clienteSel && <p style={{ textAlign: "right", color: AMBER, fontSize: 12, margin: "6px 0 0" }}>⚠ Selecciona un cliente primero (arriba)</p>}
          </div>
        )}

        {/* ── PASO 4 ── */}
        {paso === 4 && (
          <div key="paso4" className="card-lift" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 22, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", animation: "slideUp 280ms var(--ease-out)" }}>
            {resultado ? (
              <div style={{ textAlign: "center", padding: "40px 20px", animation: "slideUp 300ms var(--ease-out)" }}>
                <div style={{ fontSize: 52, marginBottom: 14 }}>🎉</div>
                <h3 style={{ color: GREEN, margin: "0 0 8px", fontSize: 20 }}>Cotización creada en buyer</h3>
                <p style={{ color: GRAY, margin: "0 0 8px", fontSize: 14 }}>
                  N° <strong>{resultado.numero_cotizacion}</strong> — estado: Pendiente
                  {resultado.moneda && resultado.moneda !== "PEN" && (
                    <span style={{ marginLeft: 8, background: "#E0F2FE", color: "#0C4A6E", fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 12 }}>
                      $ USD{resultado.tipo_cambio ? ` · TC S/ ${resultado.tipo_cambio}` : ""}
                    </span>
                  )}
                </p>
                <p style={{ color: GRAY, margin: "0 0 24px", fontSize: 13 }}>
                  Aparecerá en tu lista de cotizaciones del CRM en unos segundos.
                </p>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                  <a href={resultado.url_buyer} target="_blank" rel="noreferrer"
                    style={{ background: "linear-gradient(135deg, #1E3A5F, #3A3C7A)", boxShadow: "0 4px 14px rgba(58,60,122,0.35)", color: "#fff", padding: "11px 24px", borderRadius: 9, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
                    Ver en buyer
                  </a>
                  <a href={resultado.pdf_url} target="_blank" rel="noreferrer"
                    style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)", boxShadow: "0 4px 14px rgba(34,197,94,0.35)", color: "#fff", padding: "11px 24px", borderRadius: 9, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
                    Descargar PDF
                  </a>
                  <button onClick={resetear}
                    style={{ background: "#f3f4f6", color: GRAY, border: "1px solid #e5e7eb", borderRadius: 9, padding: "11px 20px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                    Nueva cotización
                  </button>
                </div>
              </div>
            ) : (
              <>
                <SectionTitle color={NAV} label="Productos en buyer" count={itemsVerif.length} />
                <p style={{ margin: "-6px 0 14px", color: GRAY, fontSize: 13 }}>
                  Seleccioná el producto de buyer para cada ítem. Los productos nuevos se crearán al generar la cotización.
                  <br /><span style={{ color: AMBER, fontWeight: 600 }}>⚡ Guardá el precio en buyer antes de crear la cotización si querés actualizarlo permanentemente.</span>
                </p>

                {/* Aviso si las sugerencias de este lote no usaron IA (FR-009) */}
                {modoGlobalMatch === "respaldo_palabras" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#FFFBEB", border: `1px solid ${AMBER}60`, borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 12, color: "#92400E", fontWeight: 600 }}>
                    ⚠️ El servicio de sugerencias por IA no respondió — estas sugerencias usan el matching básico por palabras. Revísalas con más cuidado.
                  </div>
                )}

                {/* Chips de filtro por estado */}
                <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                  {[
                    { key: "todos",            label: "Todos",         color: NAV },
                    { key: "encontrado",       label: "Coincidencias", color: GREEN },
                    { key: "revisar",          label: "Revisar",       color: AMBER },
                    { key: "nuevo",            label: "Nuevos",        color: AMBER },
                    { key: "pendiente_manual", label: "Sin precio",    color: GRAY },
                  ].filter(c => c.key === "todos" || conteoEstados[c.key] > 0).map(c => {
                    const activo = filtroEstado === c.key;
                    return (
                      <button key={c.key} onClick={() => setFiltroEstado(c.key)}
                        style={{
                          padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                          border: activo ? `1.5px solid ${c.color}` : "1px solid #e5e7eb",
                          background: activo ? c.color + "15" : "#fff",
                          color: activo ? c.color : GRAY, transition: "all 150ms",
                        }}>
                        {c.label} · {conteoEstados[c.key]}
                      </button>
                    );
                  })}
                </div>

                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "auto", marginBottom: 20 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 780 }}>
                    <thead>
                      <tr style={{ background: LIGHTBG }}>
                        {["Estado", "Producto pipeline", "Producto en buyer", "P.Costo S/IGV", "P.Venta S/IGV", "Cant. / Acción"].map(h => (
                          <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: GRAY, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: ".5px", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {itemsFiltrados.map(({ item, i }) => {
                        const estadoColor = { encontrado: GREEN, creado: GREEN, revisar: AMBER, nuevo: AMBER, pendiente_manual: GRAY }[item.estado] || GRAY;
                        const estadoLabel = { encontrado: "✅ Encontrado", revisar: "⚠️ Revisar", nuevo: "🆕 Nuevo", creado: "✓ Creado", pendiente_manual: "— Sin precio" }[item.estado] || item.estado;
                        const precioCambio = !!item.buyer_id && (
                          String(item.pb_edit) !== String(item.pb_original ?? "") ||
                          String(item.pv_edit) !== String(item.pv_original ?? "")
                        );
                        return (
                          <tr key={i} className="stagger-item" style={{ borderTop: "1px solid #f3f4f6" }}>
                            {/* Estado */}
                            <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                              <span style={{ color: estadoColor, fontWeight: 700, fontSize: 11 }}>{estadoLabel}</span>
                              {item.precio_actualizado && (
                                <div style={{ fontSize: 10, color: GREEN, marginTop: 2 }}>💾 Guardado</div>
                              )}
                            </td>
                            {/* Producto pipeline */}
                            <td style={{ padding: "8px 10px", verticalAlign: "top", minWidth: 220 }}>
                              {item.estado === "nuevo" ? (
                                <input
                                  value={limpiarDescripcion(item.descripcion_proveedor || item.descripcion)}
                                  onChange={(e) => setItemsVerif(prev => {
                                    const c = [...prev];
                                    c[i] = { ...c[i], descripcion_proveedor: e.target.value };
                                    return c;
                                  })}
                                  style={{ width: "100%", border: `1px solid ${AMBER}`, borderRadius: 6, padding: "3px 6px", fontSize: 12, fontWeight: 600, outline: "none", background: "#FFFBEB", color: "#92400E", marginBottom: 3 }}
                                  title="Editar nombre antes de crear en buyer"
                                />
                              ) : (
                                <span style={{ fontWeight: 600, fontSize: 12, display: "block" }}>
                                  {limpiarDescripcion(item.descripcion_proveedor || item.descripcion)}
                                </span>
                              )}
                              <span style={{ background: "#eef0fa", color: NAV, borderRadius: 10, padding: "1px 6px", fontSize: 9, fontWeight: 700 }}>{item.categoria}</span>
                              <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>
                                Pipeline: {sym}{parseFloat(item.precio_base||0).toFixed(2)} / {sym}{parseFloat(item.precio_venta_edit||0).toFixed(2)}
                              </div>
                            </td>
                            {/* Selector buyer */}
                            <td style={{ padding: "8px 10px", verticalAlign: "top", minWidth: 230 }}>
                              {item.candidatos.length > 0 ? (
                                <select value={item.buyer_id ?? "nuevo"} onChange={(e) => cambiarBuyerSel(i, e.target.value)}
                                  style={{ width: "100%", border: `1px solid ${item.estado === "encontrado" ? GREEN + "60" : item.estado === "revisar" ? AMBER + "60" : "#e5e7eb"}`, borderRadius: 7, padding: "5px 7px", fontSize: 11, outline: "none", background: "#fff", cursor: "pointer" }}>
                                  {item.candidatos.map(c => (
                                    <option key={c.id} value={c.id}>
                                      {c.score != null ? `${Math.round(c.score * 100)}% — ` : ""}{c.descripcion}
                                    </option>
                                  ))}
                                  <option value="nuevo">🆕 Crear nuevo producto</option>
                                </select>
                              ) : (
                                <span style={{ fontSize: 11, color: GRAY, fontStyle: "italic" }}>
                                  {item.estado === "pendiente_manual" ? "Sin precio — se omitirá" : "No hay similares → se creará"}
                                </span>
                              )}
                              {item.buyer_desc && (item.estado === "encontrado" || item.estado === "revisar") && (
                                <div style={{ fontSize: 9, color: GRAY, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  en buyer: {item.buyer_desc}
                                </div>
                              )}
                              {item.razonamiento && (
                                <div style={{ fontSize: 9, color: item.modo === "respaldo_palabras" ? "#92400E" : "#6366F1", marginTop: 3, fontStyle: "italic", lineHeight: 1.3 }}
                                     title={item.razonamiento}>
                                  {item.modo === "ia" ? "🤖 " : item.modo === "manual" ? "✋ " : item.modo === "respaldo_palabras" ? "⚠️ " : ""}
                                  {item.razonamiento}
                                </div>
                              )}
                            </td>
                            {/* P.Costo editable */}
                            <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                              <input type="number" value={item.pb_edit}
                                onChange={(e) => setItemsVerif(prev => { const c=[...prev]; c[i]={...c[i],pb_edit:e.target.value,precio_actualizado:false}; return c; })}
                                style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "3px 5px", width: 76, fontSize: 12, outline: "none" }} />
                            </td>
                            {/* P.Venta editable */}
                            <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                              <input type="number" value={item.pv_edit}
                                onChange={(e) => setItemsVerif(prev => { const c=[...prev]; c[i]={...c[i],pv_edit:e.target.value,precio_actualizado:false}; return c; })}
                                style={{ border: `1px solid ${NAV}40`, borderRadius: 6, padding: "3px 5px", width: 76, fontSize: 12, fontWeight: 700, outline: "none", color: NAV }} />
                            </td>
                            {/* Cantidad + botón actualizar */}
                            <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                              <div style={{ fontSize: 12, color: GRAY }}>{item.cantidad} {item.unidad}</div>
                              {precioCambio && !item.precio_actualizado && (
                                <button onClick={() => actualizarPrecioBuyer(i)}
                                  title="Actualizar este precio en buyer permanentemente"
                                  style={{ marginTop: 4, padding: "2px 8px", borderRadius: 6, border: `1px solid ${AMBER}`, background: "#FFFBEB", color: "#92400E", fontSize: 10, fontWeight: 700, cursor: "pointer", display: "block", whiteSpace: "nowrap" }}>
                                  ⚡ Guardar precio
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <BtnSecondary onClick={() => setPaso(3)}>← Volver</BtnSecondary>
                  {!cargando && (
                    <BtnSecondary onClick={() => { setPreviewFuente("pipeline"); setShowPreviewModal(true); }}>
                      👁 Previsualizar
                    </BtnSecondary>
                  )}
                  {cargando
                    ? <Cargando texto="Creando cotización en buyer..." />
                    : <BtnPrimary onClick={iniciarCrearCotizacion} color={GREEN}>🚀 Crear cotización en buyer</BtnPrimary>
                  }
                </div>
              </>
            )}
          </div>
        )}

      </div>

      {/* ── Modal: elegir fuente de precios ──────────────────────── */}
      {showPrecioModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "28px 28px 20px",
            maxWidth: 500, width: "100%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
          }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", marginBottom: 5 }}>
              ¿Qué precios usar para la cotización?
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 22 }}>
              Algunos productos ya existen en buyer con precios distintos a los del pipeline.
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <button
                onClick={() => { setShowPrecioModal(false); crearCotizacion("pipeline"); }}
                style={{
                  flex: 1, background: "#f0fdf4", border: "2px solid #16a34a", borderRadius: 12,
                  padding: "16px 14px", cursor: "pointer", textAlign: "left",
                }}
              >
                <div style={{ fontSize: 22, marginBottom: 6 }}>🚀</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#16a34a", marginBottom: 4 }}>
                  Precios del Pipeline
                </div>
                <div style={{ fontSize: 12, color: "#374151" }}>
                  Usa los precios nuevos del proveedor (cotización actual)
                </div>
              </button>
              <button
                onClick={() => { setShowPrecioModal(false); crearCotizacion("buyer"); }}
                style={{
                  flex: 1, background: "#eff6ff", border: "2px solid #3b82f6", borderRadius: 12,
                  padding: "16px 14px", cursor: "pointer", textAlign: "left",
                }}
              >
                <div style={{ fontSize: 22, marginBottom: 6 }}>📦</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#1d4ed8", marginBottom: 4 }}>
                  Precios de Buyer
                </div>
                <div style={{ fontSize: 12, color: "#374151" }}>
                  Usa los precios registrados en el sistema
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowPrecioModal(false)}
              style={{
                background: "none", border: "none", color: "#9ca3af",
                cursor: "pointer", fontSize: 13, width: "100%", textAlign: "center",
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Modal: previsualización de la cotización ──────────────────────── */}
      {showPreviewModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "26px 26px 20px",
            maxWidth: 720, width: "100%", maxHeight: "85vh", overflow: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>Vista previa de la cotización</div>
                <div style={{ fontSize: 13, color: GRAY, marginTop: 2 }}>
                  {clienteSel?.empresa || clienteSel?.nombre}
                  {clienteSel?.ruc && ` · RUC ${clienteSel.ruc}`}
                </div>
              </div>
              <button onClick={() => setShowPreviewModal(false)}
                style={{ background: "none", border: "none", fontSize: 22, color: GRAY, cursor: "pointer", lineHeight: 1 }}>
                ×
              </button>
            </div>

            {hayExistentesPreview && (
              <div style={{ display: "flex", gap: 8, margin: "16px 0 6px" }}>
                {[
                  { val: "pipeline", label: "🚀 Precios del Pipeline", color: GREEN,  bg: "#f0fdf4" },
                  { val: "buyer",    label: "📦 Precios de Buyer",     color: "#1d4ed8", bg: "#eff6ff" },
                ].map(opt => {
                  const activo = previewFuente === opt.val;
                  return (
                    <button key={opt.val} onClick={() => setPreviewFuente(opt.val)}
                      style={{
                        flex: 1, padding: "9px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                        border: activo ? `2px solid ${opt.color}` : "1px solid #e5e7eb",
                        background: activo ? opt.bg : "#fff",
                        color: activo ? opt.color : GRAY,
                      }}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", marginTop: 14 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: LIGHTBG }}>
                    {["Producto", "Cant.", `P. Unit. ${sym}`, `Subtotal ${sym}`].map(h => (
                      <th key={h} style={{ padding: "7px 10px", textAlign: h === "Producto" ? "left" : "right", color: GRAY, fontWeight: 600, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.incluidas.map((l, idx) => (
                    <tr key={idx} style={{ borderTop: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "7px 10px" }}>
                        {l.descripcion}
                        {l.esNuevo && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: AMBER }}>NUEVO</span>}
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>{l.cantidad} {l.unidad}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{l.pv.toFixed(2)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700 }}>{l.subtotal.toFixed(2)}</td>
                    </tr>
                  ))}
                  {previewData.incluidas.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: "16px 10px", textAlign: "center", color: GRAY, fontStyle: "italic" }}>No hay productos para cotizar</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {previewData.omitidas.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11, color: "#92400E", background: "#FFFBEB", border: `1px solid ${AMBER}40`, borderRadius: 8, padding: "8px 10px" }}>
                ⚠ {previewData.omitidas.length} producto(s) sin precio no se incluirán en la cotización.
              </div>
            )}

            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #f3f4f6", display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
              <div style={{ fontSize: 13, color: GRAY }}>Subtotal: {sym} {previewSubtotal.toFixed(2)}</div>
              <div style={{ fontSize: 13, color: GRAY }}>IGV: {sym} {previewIgv.toFixed(2)}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: NAV }}>Total: {sym} {previewTotal.toFixed(2)}</div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <BtnSecondary onClick={() => setShowPreviewModal(false)}>Seguir editando</BtnSecondary>
              <BtnPrimary color={GREEN} disabled={previewData.incluidas.length === 0}
                onClick={() => { setShowPreviewModal(false); crearCotizacion(previewFuente); }}>
                🚀 Crear cotización en buyer
              </BtnPrimary>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
